import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BrutalButton from "@/components/common/BrutalButton";

export default function Register() {
    const { register, user } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user && typeof user === "object") navigate("/");
    }, [user, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (form.password.length < 6) {
            setError("Password must be at least 6 characters.");
            return;
        }
        setLoading(true);
        const res = await register(form.name, form.email, form.password);
        setLoading(false);
        if (res.ok) {
            toast.success("Account created");
            navigate("/");
        } else {
            setError(res.error);
        }
    };

    return (
        <section data-testid="register-page" className="py-20 md:py-28">
            <div className="max-w-md mx-auto px-6">
                <div className="brutal-card bg-white p-8 md:p-10">
                    <span className="label-mono inline-block bg-brand-pink border-2 border-ink rounded-full px-3 py-1">
                        Create account
                    </span>
                    <h1 className="mt-5 font-display text-4xl font-black tracking-tighter">
                        Join the studio.
                    </h1>
                    <p className="mt-2 font-body text-ink/65">
                        Create an account to track your project and chat with our team.
                    </p>

                    <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
                        <label className="block">
                            <span className="label-mono mb-2 block">Full name</span>
                            <input
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="brutal-input w-full"
                                placeholder="Lia Park"
                                data-testid="register-input-name"
                            />
                        </label>
                        <label className="block">
                            <span className="label-mono mb-2 block">Email</span>
                            <input
                                required
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="brutal-input w-full"
                                placeholder="you@company.com"
                                data-testid="register-input-email"
                            />
                        </label>
                        <label className="block">
                            <span className="label-mono mb-2 block">Password</span>
                            <input
                                required
                                type="password"
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                className="brutal-input w-full"
                                placeholder="At least 6 characters"
                                data-testid="register-input-password"
                            />
                        </label>
                        {error && (
                            <div data-testid="register-error" className="bg-brand-orange text-white border-2 border-ink rounded-lg px-4 py-3 font-body text-sm">
                                {error}
                            </div>
                        )}
                        <BrutalButton
                            type="submit"
                            variant="primary"
                            size="lg"
                            className="w-full"
                            disabled={loading}
                            data-testid="register-submit-button"
                        >
                            {loading ? "Creating…" : "Create account"} <ArrowRight size={16} />
                        </BrutalButton>
                    </form>

                    <p className="mt-6 font-body text-sm text-ink/65">
                        Already have an account?{" "}
                        <Link to="/login" className="font-bold underline underline-offset-4" data-testid="register-login-link">
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </section>
    );
}
